import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '../../../shared/component-base';
import { ServiceByApi } from '../../services/models/service';
import {
  NotificationByApi,
  NotificationType,
} from '../../services/models/user';
import { RouterHandler } from '../../services/router-handler-service';
import { getServiceTypeUrl } from '../../services/utility';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent extends BaseComponent implements OnInit {
  @ViewChild('langSelectDOM', { static: true, read: ElementRef })
  langSelectDOM: ElementRef<HTMLElement> | undefined;
  showMenu = false;
  NotificationType = NotificationType;

  @Input() contrastColor = false;

  notificationList = signal<NotificationByApi[]>([]);
  newNotifications = signal(false);

  readonly NOTI_STORAGE_KEY = 'LAST_SEEN_NOTIFICATION_ID';

  public override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    if (this.isLoggedIn()) {
      this.startNotifPolling();
    }

    this.restApiService.userLoggedIn$.subscribe(() => {
      this.restApiService.stopNotificationPolling();
      this.startNotifPolling();
    });

    this.restApiService.userLoggedOut$.subscribe(() => {
      this.restApiService.stopNotificationPolling();
      this.notificationList.set([]);
      this.newNotifications.set(false);
      localStorage.removeItem(this.NOTI_STORAGE_KEY);
    });
  }

  private startNotifPolling(): void {
    this.restApiService.subscribeToNotifications().subscribe(notifs => {
      if (!this.isLoggedIn()) return;
      
      this.notificationList().unshift(...notifs.notifications);

      if (this.notificationList()[0]) {
        const latestNotiId = this.notificationList()[0].id.toString();

        if (localStorage.getItem(this.NOTI_STORAGE_KEY) !== latestNotiId) {
          this.newNotifications.set(true);
        }
      }

      //console.log('noti', this.notificationList())
    });
  }

  toggleNotificationPanel(): void {
    this.showMenu = !this.showMenu;

    if (this.showMenu && this.notificationList()[0]) {
      const latestNotiId = this.notificationList()[0].id.toString();
      localStorage.setItem(this.NOTI_STORAGE_KEY, latestNotiId);
      this.newNotifications.set(false);
    }
  }

  deleteNotification(id: number): void {
    const index = this.notificationList().findIndex(n => n.id === id);
    if (index > -1){
      this.notificationList().splice(index, 1);
    }
    this.restApiService.deleteNotification(id).subscribe(() => {
      console.log('Notification removed: ', id);
    });
  }

  deleteAllNotifications(): void {
    const latestNoti = this.notificationList()[0];
    this.restApiService.deleteAllNotifications(latestNoti.id).subscribe(() => {
      console.log('All notifications cleared');
      this.notificationList.set([]);
    });
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent): void {
    if (
      this.langSelectDOM &&
      !this.langSelectDOM.nativeElement.contains(event.target as Node)
    ) {
      this.showMenu = false;
    }
  }

  getIconSrc(typeKey: string): string {
    switch (typeKey) {
      case NotificationType.PROGRAM_DELETED:
      case NotificationType.PROGRAM_UPDATED:
      case NotificationType.PROGRAM_RECOMMEND:
      case NotificationType.PROGRAM_INTERESTED:
      case NotificationType.PROGRAM_JOINED:
        return 'owner-menu-icon/owner-menu-4.svg';
      case NotificationType.AD_MESSAGE:
        return 'owner-menu-icon/owner-menu-2.svg';
      case NotificationType.BLOG_NEW:
        return 'ui/info-icon.svg';
      case NotificationType.KENNEL_JOINED:
      case NotificationType.KENNEL_REQUEST:
        return 'owner-menu-icon/owner-menu-1.svg';
      case NotificationType.SERVICE_REVIEWED:
      case NotificationType.PROGRAM_REVIEWED:
      case NotificationType.PROGRAM_REVIEW_REPLIED:
      case NotificationType.SERVICE_REVIEW_REPLIED:
        return 'owner-menu-icon/owner-menu-4.svg';
      case NotificationType.COMMENT_POSTED:
      case NotificationType.COMMENT_REPLY:
        return 'owner-menu-icon/owner-menu-1.svg';
      default:
        return '';
    }
  }

  getKennelRequestTranslateParams(title_log_args: string[]): {
    userName: string;
    dogName: string;
  } {
    return {
      userName: title_log_args[0],
      dogName: title_log_args[1],
    };
  }
  getKennelJoinedTranslateParams(title_log_args: string[]): {
    dogName: string;
  } {
    return {
      dogName: title_log_args[1],
    };
  }
  getProgramInterestedTranslateParams(title_log_args: string[]): {
    userName: string;
    programName: string;
  } {
    return {
      userName: title_log_args[0],
      programName: title_log_args[1],
    };
  }

  readonly router = inject(Router);
  readonly routerHandler = inject(RouterHandler);
  goToProvider(id: number, commentId?: number) {
    let fragment = {};
    if (commentId) {
      fragment = { r: commentId };
    }
    this.restApiService.getService(id).subscribe({
      next: value => {
        const provider = value as ServiceByApi;
        const parsedServiceType = getServiceTypeUrl(provider.type);
        this.router.navigate(
          ['/', this.currentLang(), 'provider', parsedServiceType, provider.id],
          { fragment: this.routerHandler.fragmentToRaw(fragment) }
        );
      },
      error: err => {
        console.log('error: ', err);
      },
    });
  }

  goToProgram(id: number, commentId?: number) {
    let fragment = {};
    if (commentId) {
      fragment = { r: commentId };
    }
    this.router.navigate(['/', this.currentLang(), 'programs', id], {
      fragment: this.routerHandler.fragmentToRaw(fragment),
    });
  }

  goToComment(id: number){
    this.router.navigate(['/', this.currentLang(), 'comment', id]);
  }
}
